import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

interface UseReportSSEOptions {
  reportId: string;
  enabled: boolean;
}

/**
 * Subscribes to real-time report status updates via Server-Sent Events.
 * Falls back gracefully — if SSE fails, the existing refetchInterval polling
 * on the report query handles updates.
 */
export function useReportSSE({ reportId, enabled }: UseReportSSEOptions) {
  const queryClient = useQueryClient();
  const eventSourceRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (!enabled || !reportId) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    const url = `${API_URL}/reports/${reportId}/stream?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);
    eventSourceRef.current = es;

    es.addEventListener('status', (e) => {
      try {
        const data = JSON.parse(e.data);

        // Update the React Query cache with the new status
        queryClient.setQueryData(['report', reportId], (old: any) => {
          if (!old) return old;
          return { ...old, status: data.status };
        });

        // If we got a terminal status, close the connection and refetch for full data
        if (data.status === 'PASSED' || data.status === 'FAILED' || data.status === 'ERROR') {
          es.close();
          queryClient.invalidateQueries({ queryKey: ['report', reportId] });
          queryClient.invalidateQueries({ queryKey: ['reports'] });
        }
      } catch {
        // Ignore parse errors
      }
    });

    es.onerror = () => {
      // SSE failed — close silently; polling fallback will handle updates
      es.close();
    };

    return () => {
      es.close();
      eventSourceRef.current = null;
    };
  }, [reportId, enabled, queryClient]);
}
